import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  TablePagination
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Close
} from '@mui/icons-material';
import { apiService } from '../services/api';

const AdminServicesPage = () => {
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [allServices, setAllServices] = useState([]);
  const [serviceData, setServiceData] = useState({
    name: '',
    code: '',
    description: '',
    price: '',
    duration: '',
    status: 'active',
    requirements: ''
  });

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const response = await apiService.getServices();
      const servicesData = response.data;
      
      // Handle both array and paginated response
      let servicesList = Array.isArray(servicesData) ? servicesData : (servicesData.results || []);
      servicesList = Array.isArray(servicesList) ? servicesList : [];
      
      setAllServices(servicesList);
      setTotalCount(servicesList.length);
      
      console.log('Services loaded:', servicesList.length);
    } catch (error) {
      console.error('Error loading services:', error);
      setAllServices([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Get paginated services
  const paginatedServices = allServices.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleAddService = () => {
    setEditingService(null);
    setServiceData({
      name: '',
      code: '',
      description: '',
      price: '',
      duration: '',
      status: 'active',
      requirements: ''
    });
    setOpenDialog(true);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setServiceData({
      name: service.name,
      code: service.code,
      description: service.description,
      price: service.price,
      duration: service.duration,
      status: service.status,
      requirements: service.requirements || ''
    });
    setOpenDialog(true);
  };

  const handleDeleteService = async (service) => {
    if (window.confirm(`Are you sure you want to delete "${service.name}"?`)) {
      try {
        await apiService.deleteService(service.id);
        await loadServices();
        // If current page becomes empty after deletion, go to previous page
        if (paginatedServices.length === 1 && page > 0) {
          setPage(page - 1);
        }
        alert('Service deleted successfully!');
      } catch (error) {
        console.error('Error deleting service:', error);
        alert('Failed to delete service. Please try again.');
      }
    }
  };

  const handleSaveService = async () => {
    try {
      if (editingService) {
        await apiService.updateService(editingService.id, serviceData);
      } else {
        await apiService.createService(serviceData);
      }
      await loadServices();
      setOpenDialog(false);
      alert(`Service ${editingService ? 'updated' : 'created'} successfully!`);
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Failed to save service. Please try again.');
    }
  };

  const handleToggleStatus = async (service) => {
    try {
      const newStatus = service.status === 'active' ? 'inactive' : 'active';
      await apiService.updateService(service.id, { ...service, status: newStatus });
      await loadServices();
    } catch (error) {
      console.error('Error toggling service status:', error);
      alert('Failed to update service status. Please try again.');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleInputChange = (field) => (event) => {
    setServiceData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Services Management</Typography>
        <Button 
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddService}
          sx={{ bgcolor: '#1976d2' }}
        >
          Add Service
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 600 }}>Service Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">Loading services...</Typography>
                </TableCell>
              </TableRow>
            ) : allServices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">No services found. Create your first service!</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedServices.map((service) => (
                <TableRow key={service.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 500 }}>{service.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {service.description?.substring(0, 60)}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={service.code} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, color: '#1976d2' }}>
                      ₹{parseFloat(service.price).toLocaleString('en-IN')}
                    </Typography>
                  </TableCell>
                  <TableCell>{service.duration}</TableCell>
                  <TableCell>
                    <Chip 
                      label={service.status === 'active' ? 'Active' : 'Inactive'}
                      color={service.status === 'active' ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleToggleStatus(service)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <IconButton 
                      size="small" 
                      onClick={() => handleEditService(service)}
                      sx={{ color: '#1976d2' }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteService(service)}
                      sx={{ color: '#d32f2f' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {!loading && totalCount > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #e0e0e0', mt: 2 }}
        />
      )}

      {/* Add/Edit Service Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{editingService ? 'Edit Service' : 'Add New Service'}</Typography>
          <IconButton onClick={() => setOpenDialog(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              fullWidth
              label="Service Name"
              value={serviceData.name}
              onChange={handleInputChange('name')}
              required
              placeholder="e.g., GST Registration"
            />
            
            <TextField
              fullWidth
              label="Service Code"
              value={serviceData.code}
              onChange={handleInputChange('code')}
              required
              placeholder="e.g., GST-REG"
              helperText="Unique identifier for this service"
            />
            
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={serviceData.description}
              onChange={handleInputChange('description')}
              required
              placeholder="Detailed description of the service..."
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Price (INR)"
                type="number"
                value={serviceData.price}
                onChange={handleInputChange('price')}
                required
                placeholder="5000"
              />
              
              <TextField
                fullWidth
                label="Duration"
                value={serviceData.duration}
                onChange={handleInputChange('duration')}
                required
                placeholder="e.g., 7-10 days"
              />
            </Box>
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={serviceData.status}
                onChange={handleInputChange('status')}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Requirements"
              multiline
              rows={3}
              value={serviceData.requirements}
              onChange={handleInputChange('requirements')}
              placeholder="List required documents or information..."
              helperText="Optional: Specify what documents/info are needed"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveService}
            disabled={!serviceData.name || !serviceData.code || !serviceData.price || !serviceData.duration}
          >
            {editingService ? 'Update Service' : 'Create Service'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminServicesPage;
